import { skipToken } from "@reduxjs/toolkit/query";
import type {
  AccountGradingRecording,
  GradingId,
} from "@repo/app-types/entities/teammatePerformanceSummary";
import type { FC, Ref, RefObject } from "react";
import {
  usePlaybookGradingCallOutlineQuery,
  usePlaybookGradingQuery,
} from "../../api/entities/performancePlaybook.js";
import { useRecordingToolActivitiesQuery } from "../../api/entities/recording.js";
import { useBasicTeammateQuery } from "../../api/entities/teammate.js";
import { AudioPlayer } from "../../components/audioPlayer/audioPlayer.js";
import { TranscriptScrollPane } from "../../components/transcript/transcriptScrollPane.js";
import type { QuoteMatchMap } from "../teammatePerformance/gradeValidationHelpers.js";
import {
  GradingTranscriptWithIndex,
  type GradingTranscriptWithIndexHandle,
} from "../teammatePerformance/gradingTranscriptWithIndex.js";
import { TranscriptNavigatorDropdown } from "../teammatePerformance/transcriptNavigatorDropdown.js";
import { TranscriptVideoPanel } from "../teammatePerformance/transcriptVideoPanel.js";
import { useTranscriptOutlineSync } from "../teammatePerformance/useTranscriptOutlineSync.js";

interface TranscriptViewerPaneProps {
  gradingId: GradingId;
  sortedRecordings: readonly AccountGradingRecording[];
  quoteMatches: QuoteMatchMap;
  transcriptRef: Ref<GradingTranscriptWithIndexHandle>;
  transcriptContainerRef: RefObject<HTMLDivElement | null>;
}

/**
 * Reusable right-pane transcript viewer with call outline navigation,
 * evidence highlighting, and an audio player.
 *
 * Used by the KB adherence statement slideover, signal match slideover,
 * and customer interaction marker slideover.
 */
export const TranscriptViewerPane: FC<TranscriptViewerPaneProps> = ({
  gradingId,
  sortedRecordings,
  quoteMatches,
  transcriptRef,
  transcriptContainerRef,
}) => {
  const hasTranscript = sortedRecordings.some((r) => r.transcript);
  const { data: grading } = usePlaybookGradingQuery({ gradingId });
  const { data: teammate } = useBasicTeammateQuery(
    grading?.teammate_id ?? skipToken,
  );

  // Fetch tool activities for all recordings
  const recordingIds = sortedRecordings.map((r) => r.id);
  const { data: toolActivitiesByRecordingId } = useRecordingToolActivitiesQuery(
    recordingIds.length > 0 ? recordingIds : skipToken,
  );

  const { readingPosition, scrollToTranscriptPosition } =
    useTranscriptOutlineSync({
      transcriptContainerRef,
      sortedRecordings,
    });

  // Fetch call outline for overlay navigation
  const {
    data: callOutline,
    isLoading: isCallOutlineLoading,
    isError: isCallOutlineError,
  } = usePlaybookGradingCallOutlineQuery({ gradingId });

  return (
    <>
      {/* Transcript pane */}
      <div className="flex min-h-0 flex-col bg-white">
        <TranscriptVideoPanel recordings={sortedRecordings} />
        <div className="p-4">
          <TranscriptNavigatorDropdown
            showCurrentKeyMoment
            recordings={sortedRecordings}
            callOutline={callOutline}
            isLoading={isCallOutlineLoading}
            isError={isCallOutlineError}
            readingPosition={readingPosition}
            onPositionRequest={scrollToTranscriptPosition}
          />
        </div>
        <TranscriptScrollPane
          scrollContainerRef={transcriptContainerRef}
          className="px-4"
        >
          <div className="pb-4">
            {hasTranscript ? (
              <GradingTranscriptWithIndex
                ref={transcriptRef}
                recordings={sortedRecordings}
                teammateType={teammate?.type}
                quoteMatches={quoteMatches}
                scrollToTranscriptPosition={scrollToTranscriptPosition}
                toolActivitiesByRecordingId={toolActivitiesByRecordingId}
              />
            ) : (
              <p className="text-sm text-muted-foreground">
                No transcript available
              </p>
            )}
          </div>
        </TranscriptScrollPane>
      </div>

      {/* Audio player spans full width */}
      <div className="col-span-full">
        <AudioPlayer />
      </div>
    </>
  );
};
